import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import * as fromActions from './user.actions';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, of, timer } from 'rxjs';
import { UserService } from '../service';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { logout, selectIsAuthenticated } from '@qvs/core/auth';
import { Contact, UserProfile, UserRoleEnum } from '@qvs/core';

@Injectable()
export class UserEffects {
  checkSessionInactiveInterval = 60_000;

  loadUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadUserProfile),
      switchMap(() =>
        this.userService.loadProfile().pipe(
          switchMap((userProfile) =>
            userProfile ? of(fromActions.loadUserProfileSuccess({ userProfile })) : of(fromActions.loadUserProfileFailure())
          ),
          catchError(() => of(fromActions.loadUserProfileFailure()))
        )
      )
    )
  );

  checkUserSessionIsActive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.checkUserSessionIsActive, fromActions.loadUserProfileSuccess),
      concatLatestFrom(() => this.store.select(selectIsAuthenticated)),
      filter(([_, isAuthenticated]) => isAuthenticated),
      switchMap(() =>
        this.userService.checkUserSessionIsActive().pipe(
          switchMap((isActive) => {
            if (isActive) {
              this.store.dispatch(fromActions.checkUserSessionIsActiveSuccess());
              return timer(this.checkSessionInactiveInterval).pipe(switchMap(() => of(fromActions.checkUserSessionIsActive())));
            }
            return of(fromActions.checkUserSessionIsActiveFailure());
          }),
          catchError(() => of(fromActions.checkUserSessionIsActiveFailure()))
        )
      )
    )
  );

  //TODO 21-Aug-2026/majk move the isAuthenticated filter to the SessionTimeoutInterceptor
  userSessionIsActiveFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.checkUserSessionIsActiveFailure),
        withLatestFrom(this.store.select(selectIsAuthenticated)),
        filter(([_action, isAuthenticated]) => isAuthenticated),
        tap(() => {
          this.store.dispatch(logout());
          this.router.navigateByUrl('/login/session-timed-out').finally();
        })
      ),
    { dispatch: false }
  );

  loadCrmContactImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadUserProfileSuccess),
      filter(({ userProfile }) => UserEffects.isClientUserWithCrmContact(userProfile)),
      switchMap(({ userProfile }) =>
        this.userService.loadUserProfileImage(userProfile.contacts[0].staffId).pipe(
          map((res) =>
            fromActions.updateCrmSuccess({
              userProfile: { ...userProfile, contacts: UserEffects.mapCrmPicture(userProfile.contacts, res.image) }
            })
          ),
          catchError(() => EMPTY)
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly userService: UserService,
    private readonly store: Store,
    private readonly router: Router
  ) {}

  static mapCrmPicture(contacts: Contact[], image: string): Contact[] {
    return contacts?.map((contact, index) => (index === 0 ? { ...contact, picture: image } : contact));
  }

  private static isClientUserWithCrmContact(userProfile: UserProfile): boolean {
    return userProfile?.role === UserRoleEnum.CLIENT && !!userProfile?.contacts?.[0]?.staffId;
  }
}
